import { useQuery } from '@tanstack/react-query';
import type { AnalyticsEmbedResponse } from '@time-tracking/shared';

import { parseJson } from './client.js';
import { useApiFetch } from './useApiFetch.js';

/**
 * Fetches a QuickSight embed URL.
 *
 * Deliberately never cached and never retried on success. The URL is a
 * single-use bearer token, valid five minutes and redeemable exactly once, so a
 * cached value is a value that no longer works — and a component that re-renders
 * onto a stale URL fails in a way that reads as a permissions bug.
 *
 * `gcTime: 0` matters as much as `staleTime: 0`: a token left in the query cache
 * is a live credential sitting in memory after the page has moved on.
 */
export function useAnalyticsEmbedUrl(enabled: boolean) {
  const fetch = useApiFetch();
  return useQuery<AnalyticsEmbedResponse, Error>({
    queryKey: ['analytics', 'embed-url'],
    queryFn: () => fetch('/v1/analytics/embed-url').then((r) => parseJson(r)),
    enabled,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });
}
